// src/hooks/useRealTimePrices.js
import { useCallback, useEffect, useRef, useState } from "react";

const LB_PER_SACK_60KG = 132.277357;
const DEFAULT_TRADER_FEE = 20;
const CACHE_VERSION = "v5";

function smartNumber(raw) {
  if (raw == null) return null;
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;

  let value = String(raw).trim();
  if (!value) return null;

  const scientificNotation = /^[+-]?\d+(?:\.\d+)?e[+-]?\d+$/i;

  if (scientificNotation.test(value)) {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  value = value.replace(/\s+/g, "");

  if (value.includes(",") && value.includes(".")) {
    const lastComma = value.lastIndexOf(",");
    const lastDot = value.lastIndexOf(".");

    if (lastComma > lastDot) {
      value = value.replace(/\./g, "").replace(",", ".");
    } else {
      value = value.replace(/,/g, "");
    }
  } else if (value.includes(",")) {
    value = value.replace(/\./g, "").replace(",", ".");
  } else {
    const parts = value.split(".");
    if (parts.length > 2) {
      const decimal = parts.pop();
      value = `${parts.join("")}.${decimal}`;
    }
  }

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function saneKC(value) {
  return Number.isFinite(value) && value > 0 && value < 10000
    ? value
    : null;
}

function saneUsdBrl(value) {
  return Number.isFinite(value) && value > 0 && value < 100
    ? value
    : null;
}

function clampFee(value) {
  const parsed = smartNumber(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.min(Math.max(parsed, 0), 100);
}

function todayKey() {
  const now = new Date();
  const brazilTime = new Date(now.getTime() - 3 * 60 * 60 * 1000);

  const year = brazilTime.getUTCFullYear();
  const month = String(brazilTime.getUTCMonth() + 1).padStart(2, "0");
  const day = String(brazilTime.getUTCDate()).padStart(2, "0");

  return `${year}-${month}-${day}-${CACHE_VERSION}`;
}

function readDaily(name) {
  try {
    const key = `coffee_market_daily_${name}_${todayKey()}`;
    return JSON.parse(localStorage.getItem(key) || "null");
  } catch {
    return null;
  }
}

function writeDaily(name, data) {
  try {
    const key = `coffee_market_daily_${name}_${todayKey()}`;
    localStorage.setItem(key, JSON.stringify(data));
  } catch {}
}

function rollDaily(name, currentPrice, maximumAllowed) {
  if (!Number.isFinite(currentPrice)) return null;

  const previous = readDaily(name);

  const invalidPrevious =
    !previous ||
    !Number.isFinite(previous.open) ||
    (maximumAllowed && previous.open > maximumAllowed);

  const base = invalidPrevious
    ? {
        open: currentPrice,
        high: currentPrice,
        low: currentPrice,
        last: currentPrice,
      }
    : previous;

  const next = {
    open: base.open ?? currentPrice,
    high: Math.max(base.high ?? currentPrice, currentPrice),
    low: Math.min(base.low ?? currentPrice, currentPrice),
    last: currentPrice,
  };

  writeDaily(name, next);

  const change = currentPrice - next.open;
  const changePercent = next.open ? (change / next.open) * 100 : 0;

  return {
    open: next.open,
    high: next.high,
    low: next.low,
    change,
    changePercent,
  };
}

function calculateCoffeeConversion(kcCents, usdBrl, traderFeePercent) {
  if (
    !Number.isFinite(kcCents) ||
    !Number.isFinite(usdBrl) ||
    kcCents <= 0 ||
    usdBrl <= 0
  ) {
    return null;
  }

  const feePercent = clampFee(traderFeePercent);
  const usdPerLb = kcCents / 100;
  const sackUsd = usdPerLb * LB_PER_SACK_60KG;
  const sackBrlGross = sackUsd * usdBrl;
  const feeValueBrl = sackBrlGross * (feePercent / 100);
  const sackBrlNet = sackBrlGross - feeValueBrl;

  return {
    kcCents,
    usdBrl,
    poundsPerSack: LB_PER_SACK_60KG,
    usdPerLb,
    brlPerLb: usdPerLb * usdBrl,
    sackUsd,
    sackBrlGross,
    feePercent,
    feeValueBrl,
    sackBrlNet,
  };
}

async function fetchWithTimeout(input, options = {}) {
  const { timeout = 9000, ...fetchOptions } = options;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    return await fetch(input, {
      ...fetchOptions,
      signal: controller.signal,
      cache: "no-store",
    });
  } finally {
    clearTimeout(timeoutId);
  }
}

export const useRealTimePrices = () => {
  const API_BASE = String(
    process.env.REACT_APP_API_BASE || "https://cofy.app/api"
  ).replace(/\/+$/, "");

  const QUOTE_URL = `${API_BASE}/quote.php?mode=fast`;
  const KC_JSON_URL = `${API_BASE}/data/oncafe_kc.json`;
  const DOLLAR_JSON_URL = `${API_BASE}/data/oncafe_dol.json`;

  const [coffeeData, setCoffeeData] = useState(null);
  const [dollarData, setDollarData] = useState(null);

  // Altere somente este valor no código para definir a taxa/desconto interno.
  const traderFeePercent = DEFAULT_TRADER_FEE;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fastTimerRef = useRef(null);
  const slowTimerRef = useRef(null);

  const getQuote = useCallback(async () => {
    const response = await fetchWithTimeout(QUOTE_URL, {
      headers: { Accept: "application/json" },
      timeout: 9000,
    });

    if (!response.ok) {
      throw new Error(`quote.php retornou HTTP ${response.status}`);
    }

    const data = await response.json();

    const kcCents = saneKC(
      smartNumber(
        data?.kc_cents_per_lb ??
          data?.kc_price ??
          data?.coffee_price
      )
    );

    const usdBrl = saneUsdBrl(
      smartNumber(
        data?.usdt_brl ??
          data?.usd_brl ??
          data?.dollar_brl
      )
    );

    const coffeeStats = Number.isFinite(kcCents)
      ? rollDaily("KC", kcCents, 10000)
      : null;

    const dollarStats = Number.isFinite(usdBrl)
      ? rollDaily("USDBRL", usdBrl, 100)
      : null;

    const coffeeOut = Number.isFinite(kcCents)
      ? {
          symbol: data?.symbol || "KC=F",
          name: "Coffee C",
          price: kcCents,
          ...coffeeStats,
          currency: "USD",
          unit: "¢/lb",
          exchange: "ICE",
          source: "quote.php",
          timestamp:
            data?.timestamp ||
            data?.saved_at ||
            new Date().toISOString(),
        }
      : null;

    const dollarOut = Number.isFinite(usdBrl)
      ? {
          symbol: "USD/BRL",
          name: "Dólar",
          price: usdBrl,
          ...dollarStats,
          variation: dollarStats?.changePercent ?? null,
          variationType:
            dollarStats?.changePercent > 0
              ? "positive"
              : dollarStats?.changePercent < 0
              ? "negative"
              : "neutral",
          currency: "BRL",
          source: "quote.php",
          timestamp:
            data?.timestamp ||
            data?.saved_at ||
            new Date().toISOString(),
        }
      : null;

    return { coffeeOut, dollarOut };
  }, [QUOTE_URL]);

  const getRealtimeFromJson = useCallback(async () => {
    const results = await Promise.allSettled([
      fetchWithTimeout(KC_JSON_URL, {
        headers: { Accept: "application/json" },
        timeout: 4000,
      }),
      fetchWithTimeout(DOLLAR_JSON_URL, {
        headers: { Accept: "application/json" },
        timeout: 4000,
      }),
    ]);

    const kcResponse =
      results[0].status === "fulfilled" ? results[0].value : null;

    const dollarResponse =
      results[1].status === "fulfilled" ? results[1].value : null;

    let kcJson = null;
    let dollarJson = null;

    if (kcResponse?.ok) {
      try {
        kcJson = await kcResponse.json();
      } catch {
        kcJson = null;
      }
    }

    if (dollarResponse?.ok) {
      try {
        dollarJson = await dollarResponse.json();
      } catch {
        dollarJson = null;
      }
    }

    let coffeeOut = null;
    let dollarOut = null;

    if (kcJson) {
      const price = saneKC(
        smartNumber(
          kcJson.price ??
            kcJson.kc_cents_per_lb ??
            kcJson.last
        )
      );

      if (Number.isFinite(price)) {
        const open = saneKC(smartNumber(kcJson.open)) ?? price;
        const high = saneKC(smartNumber(kcJson.high)) ?? price;
        const low = saneKC(smartNumber(kcJson.low)) ?? price;

        const informedChange = smartNumber(
          kcJson.change_abs ??
            kcJson.change
        );

        const informedChangePercent = smartNumber(
          kcJson.change_pct ??
            kcJson.change_percent
        );

        const calculatedChange = price - open;
        const calculatedChangePercent = open
          ? (calculatedChange / open) * 100
          : 0;

        coffeeOut = {
          symbol: kcJson.symbol || "KC=F",
          name: "Coffee C",
          price,
          open,
          high,
          low,
          change: Number.isFinite(informedChange)
            ? informedChange
            : calculatedChange,
          changePercent: Number.isFinite(informedChangePercent)
            ? informedChangePercent
            : calculatedChangePercent,
          currency: "USD",
          unit: "¢/lb",
          exchange: "ICE",
          source: "oncafe_kc.json",
          timestamp:
            kcJson.data_hora ||
            kcJson.saved_at ||
            kcJson.timestamp ||
            new Date().toISOString(),
        };
      }
    }

    if (dollarJson) {
      const price = saneUsdBrl(
        smartNumber(
          dollarJson.price ??
            dollarJson.usd_brl ??
            dollarJson.usdt_brl ??
            dollarJson.last
        )
      );

      if (Number.isFinite(price)) {
        const open = saneUsdBrl(smartNumber(dollarJson.open)) ?? price;
        const high = saneUsdBrl(smartNumber(dollarJson.high)) ?? price;
        const low = saneUsdBrl(smartNumber(dollarJson.low)) ?? price;

        const informedChange = smartNumber(
          dollarJson.change_abs ??
            dollarJson.change
        );

        const informedChangePercent = smartNumber(
          dollarJson.change_pct ??
            dollarJson.change_percent
        );

        const calculatedChange = price - open;
        const calculatedChangePercent = open
          ? (calculatedChange / open) * 100
          : 0;

        const changePercent = Number.isFinite(informedChangePercent)
          ? informedChangePercent
          : calculatedChangePercent;

        dollarOut = {
          symbol: "USD/BRL",
          name: "Dólar",
          price,
          open,
          high,
          low,
          change: Number.isFinite(informedChange)
            ? informedChange
            : calculatedChange,
          changePercent,
          variation: changePercent,
          variationType:
            changePercent > 0
              ? "positive"
              : changePercent < 0
              ? "negative"
              : "neutral",
          currency: "BRL",
          source: "oncafe_dol.json",
          timestamp:
            dollarJson.data_hora ||
            dollarJson.saved_at ||
            dollarJson.timestamp ||
            new Date().toISOString(),
        };
      }
    }

    return { coffeeOut, dollarOut };
  }, [KC_JSON_URL, DOLLAR_JSON_URL]);

  const fetchAll = useCallback(
    async (mode = "full") => {
      if (mode === "full") {
        setLoading(true);
        setError(null);
      }

      try {
        const result =
          mode === "ticker"
            ? await getRealtimeFromJson()
            : await getQuote();

        if (result.coffeeOut) setCoffeeData(result.coffeeOut);
        if (result.dollarOut) setDollarData(result.dollarOut);

        setLastUpdate(new Date());
        setError(null);
      } catch (fetchError) {
        console.warn(
          "[useRealTimePrices] erro:",
          fetchError?.message || fetchError
        );

        if (mode === "full") {
          setError("Falha ao obter as cotações do café e do dólar.");
        }
      } finally {
        if (mode === "full") {
          setLoading(false);
        }
      }
    },
    [getQuote, getRealtimeFromJson]
  );

  useEffect(() => {
    fetchAll("full");

    fastTimerRef.current = setInterval(() => {
      fetchAll("ticker");
    }, 1000);

    slowTimerRef.current = setInterval(() => {
      fetchAll("full");
    }, 2 * 60 * 1000);

    return () => {
      if (fastTimerRef.current) clearInterval(fastTimerRef.current);
      if (slowTimerRef.current) clearInterval(slowTimerRef.current);
    };
  }, [fetchAll]);

  const refreshPrices = useCallback(() => {
    fetchAll("full");
  }, [fetchAll]);

  const coffeeConversion = calculateCoffeeConversion(
    coffeeData?.price,
    dollarData?.price,
    traderFeePercent
  );

  return {
    coffeeData,
    dollarData,
    coffeeConversion,
    traderFeePercent,
    loading,
    error,
    lastUpdate,
    refreshPrices,
  };
};

export default useRealTimePrices;
